import { EmptyView, ErrorView, ListControls, LoadingView, StatusBadge, DashStyles as Styles, TableWrap } from '../../components/dashboard/DashboardUI';
import { usePaginatedList } from '../../components/dashboard/usePaginatedList';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { Label } from '@app/engine';
import { FormatTimestamp, WrapLTR } from '@shared/common';
import { CallClient, Telemetry } from '@shared/frontend';
import { useNavigate } from 'react-router-dom';

/**
 * Calls list screen: a server-paginated, searchable list of the tenant's calls.
 *
 * @returns The calls list element.
 */
export function Calls() {
  const { tenantID } = useDashboard();
  const navigate = useNavigate();
  const list = usePaginatedList(
    (signal, offset, limit, pattern) => CallClient.ListCalls(signal, tenantID!, offset, limit, pattern),
    (signal, pattern) => CallClient.CountCalls(signal, tenantID!, pattern),
    [tenantID]
  );

  return (
    <>
      <ListControls
        searchValue={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={Localized.CallsSearchPlaceholder}
        offset={list.offset}
        limit={list.limit}
        total={list.total}
        pageCount={list.data.length}
        onOffsetChange={list.setOffset}
        onLimitChange={list.setLimit}
        loading={list.loading}
      />
      {list.loading ? (
        <LoadingView />
      ) : list.error ? (
        <ErrorView onRetry={list.reload} />
      ) : list.data.length === 0 ? (
        <EmptyView message={Localized.CallsNone} />
      ) : (
        <TableWrap>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>
                  <Label text={Localized.CallsFrom} />
                </th>
                <th>
                  <Label text={Localized.SharedStatus} />
                </th>
                <th className={Styles.cellFill}>
                  <Label text={Localized.CallsSummary} />
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((call) => (
                <tr
                  key={call.id}
                  className={Styles.rowClickable}
                  onClick={() => {
                    Telemetry.CallDetailOpened(call.status, call.intent, call.durationSeconds);
                    navigate(Constants.Route.CallDetail.replace(`:${Constants.Param.CallID}`, call.id));
                  }}
                >
                  <td className={Styles.cellNoWrap}>
                    {/* Caller number with the call time stacked beneath it - one compact identity cell instead of a separate When column. */}
                    <div className={Styles.cellStack}>
                      <Label text={WrapLTR(call.fromE164)} />
                      <Label classNameContainer={Styles.muted} text={FormatTimestamp(call.startedAt)} />
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={call.status} />
                  </td>
                  <td>
                    <Label classNameContainer={Styles.muted} text={call.summary ?? '—'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </>
  );
}
